<template>
  <div>
  <div class="page-with-subnav">
    <app-sub-navigation-bar
      back-label="Back to all customers"
      @back="$router.push('/contact')"
    />
    <div class="main-container page-with-subnav__content">
    <div class="ui grid customer-form-grid" :class="{ 'customer-form-grid--documents': activeTab !== 'details' }">
      <div class="sixteen wide column">
        <h1><i class="address book outline icon"></i> Edit Customer</h1>
      </div>
      <div class="sixteen wide column">
        <app-form-error></app-form-error>

        <div class="ui form">
          <div class="field settings">
            <label>Customer Name *</label>
            <app-text-input placeholder="Enter person or company name" type="text" v-model="contact.company" :error="!contact.company && submitted" />
          </div>

          <div class="ui top attached tabular menu">
            <div class="item" :class="{ active: activeTab === 'details' }" @click="activeTab = 'details'" data-tab="details">Details</div>
            <div class="item" :class="{ active: activeTab === 'quotes' }" @click="activeTab = 'quotes'" data-tab="quotes">Quotes</div>
            <div class="item" :class="{ active: activeTab === 'invoices' }" @click="activeTab = 'invoices'" data-tab="invoices">Invoices</div>
          </div>

          <div class="ui bottom attached tab segment" :class="{ active: activeTab === 'details' }" v-show="activeTab === 'details'" data-tab="details">
            <h4 class="ui dividing header">Primary Contact</h4>
            <div class="two fields">
              <div class="field settings">
                <label>First Name</label>
                <app-text-input placeholder="Enter first name" type="text" v-model="contact.first_name" />
              </div>
              <div class="field settings">
                <label>Last Name</label>
                <app-text-input placeholder="Enter last name" type="text" v-model="contact.last_name" />
              </div>
            </div>
            <div class="three fields">
              <div class="field settings">
                <label>Phone Number</label>
                <app-text-input placeholder="Enter phone number" type="text" v-model="contact.phone" />
              </div>
              <div class="field settings">
                <label>Mobile Number</label>
                <app-text-input placeholder="Enter mobile number" type="text" v-model="contact.mobile" />
              </div>
              <div class="field settings">
                <label>Email Address *</label>
                <app-text-input placeholder="Enter email address" type="email" v-model="contact.email" :error="!contact.email && submitted" />
              </div>
            </div>

            <h4 class="ui dividing header">Physical Address</h4>
            <div class="field settings">
              <label>Street Address</label>
              <app-text-input placeholder="Enter street address" type="text" v-model="contact.street_address" />
            </div>
            <div class="three fields">
              <div class="field settings">
                <label>Suburb</label>
                <app-text-input placeholder="Enter suburb" type="text" v-model="contact.suburb" />
              </div>
              <div class="field settings">
                <label>Postcode</label>
                <app-text-input placeholder="Enter postcode" type="text" v-model="contact.postcode" />
              </div>
              <div class="field settings">
                <label>State</label>
                <app-select
                  :defaultLabel="'Select state'"
                  :items="$store.getters['getStates']"
                  :value="contact.state || ''"
                  @selected="contact.state = $event.id"
                />
              </div>
            </div>

            <h4 class="ui dividing header">Postal Address</h4>
            <div class="field settings">
              <app-checkbox v-model="postalSameAsPhysical" label="Same as Physical Address" />
            </div>
            <template v-if="!postalSameAsPhysical">
              <div class="field settings">
                <label>Street Address</label>
                <app-text-input placeholder="Enter street address" type="text" v-model="contact.postal_street_address" />
              </div>
              <div class="three fields">
                <div class="field settings">
                  <label>Suburb</label>
                  <app-text-input placeholder="Enter suburb" type="text" v-model="contact.postal_suburb" />
                </div>
                <div class="field settings">
                  <label>Postcode</label>
                  <app-text-input placeholder="Enter postcode" type="text" v-model="contact.postal_postcode" />
                </div>
                <div class="field settings">
                  <label>State</label>
                  <app-select
                    :defaultLabel="'Select state'"
                    :items="$store.getters['getStates']"
                    :value="contact.postal_state || ''"
                    @selected="contact.postal_state = $event.id"
                  />
                </div>
              </div>
            </template>

            <h4 class="ui dividing header">Financial Information</h4>
            <div class="two fields">
              <div class="field settings">
                <label>ABN</label>
                <app-text-input placeholder="Enter ABN" type="text" v-model="contact.abn" />
              </div>
              <div class="field settings">
                <label>ACN</label>
                <app-text-input placeholder="Enter ACN" type="text" v-model="contact.acn" />
              </div>
            </div>

            <div class="field customer-form-actions">
              <button type="button" class="ui primary button" @click="save">Save Changes</button>
              <button type="button" class="ui negative basic button" @click="deleteCustomer">Delete Customer</button>
            </div>
          </div>
        </div>

        <div class="ui bottom attached tab segment customer-documents-tab" :class="{ active: activeTab === 'quotes' }" v-show="activeTab === 'quotes'" data-tab="quotes">
          <document-list-view
            :rows="quoteListRows"
            :loading="quotesLoading"
            :headers="quoteListHeaders"
            secondary-date-label="Expiry"
            @select="viewQuote($event.id)"
          />
          <div class="pagination-controls" style="margin-top: 1rem;">
            <app-pagination
              v-if="quotesPagination && quotesPagination.totalPages > 1"
              :page="quotesPagination.page"
              :total-pages="quotesPagination.totalPages"
              @change="fetchQuotes"
            />
          </div>
        </div>

        <div class="ui bottom attached tab segment customer-documents-tab" :class="{ active: activeTab === 'invoices' }" v-show="activeTab === 'invoices'" data-tab="invoices">
          <document-list-view
            :rows="invoiceListRows"
            :loading="invoicesLoading"
            :headers="invoiceListHeaders"
            secondary-date-label="Due"
            @select="viewInvoice"
          />
          <div class="pagination-controls" style="margin-top: 1rem;">
            <app-pagination
              v-if="invoicesPagination && invoicesPagination.totalPages > 1"
              :page="invoicesPagination.page"
              :total-pages="invoicesPagination.totalPages"
              @change="fetchInvoices"
            />
          </div>
        </div>
      </div>
    </div>
    </div>
  </div>

  <quote-preview
    v-if="quotePreviewVisible"
    :parent_quote="previewQuote"
    :parent_quote_items="previewQuoteItems"
    :parent_contacts="[]"
    :parent_contact="previewQuoteContact"
    :parent_save="savePreviewQuote"
    :summary="true"
    @send="sendQuoteButton"
    @duplicate="initDuplicate"
    @option-change="onPreviewOptionChange"
    @close="closeQuotePreview"
    @quote="onPreviewQuoteUpdate" />

  <duplicate-option-modal
    v-if="duplicateChooserVisible"
    :visible="duplicateChooserVisible"
    :option-count="(previewQuote && previewQuote.options && previewQuote.options.length) || 1"
    @close="closeDuplicateChooser"
    @cancel="closeDuplicateChooser"
    @variation="onDuplicateVariation"
    @new-quote="onDuplicateNewQuote" />

  <option-modal
    v-if="quoteOptionModalVisible"
    :content="previewQuote"
    @close="quoteOptionModalVisible = false"
    @cancel="quoteOptionModalVisible = false; quotePreviewVisible = true"
    @email="quoteSendModalVisible = true"
    @download="downloadQuotePdf" />

  <app-send-modal
    v-if="quoteSendModalVisible"
    :parent_quote="previewQuote"
    :parent_quote_items="previewQuoteItems"
    :parent_contact="previewQuoteContact"
    :parent_save="savePreviewQuote"
    @back="sendQuoteBackButton"
    @close="quoteSendModalVisible = false; quoteOptionModalVisible = false"
    @quote="onPreviewQuoteUpdate" />

  <app-customer
    v-if="customerModalVisible"
    ref="duplicateCustomer"
    :parent_quote="previewQuote"
    duplicate
    @duplicate="duplicateQuote"
    @back="customerModalVisible = false; quotePreviewVisible = true" />

  <invoice-preview
    v-if="invoicePreviewVisible"
    :content="previewInvoice"
    @sendOption="openInvoiceSendOption"
    @close="closeInvoicePreview" />

  <option-modal
    v-if="invoiceOptionModalVisible"
    title="Send Invoice"
    :content="previewInvoice"
    @close="invoiceOptionModalVisible = false"
    @cancel="invoiceOptionModalVisible = false; invoicePreviewVisible = true"
    @email="openInvoiceEmail"
    @download="downloadInvoicePdf" />

  <email
    v-if="invoiceEmailVisible"
    :content="previewInvoice"
    document-type="invoice"
    @close="closeInvoiceEmail"
    @sent="onInvoiceEmailSent" />
  </div>
</template>

<script>
  import axios from 'axios'
  import moment from 'moment'
  import swal from 'sweetalert2'
  import QuotePreview from '../../components/quote/modals/preview/preview.vue'
  import InvoicePreview from '../invoice/Preview.vue'
  import SubNavigationBar from '../../components/common/SubNavigationBar.vue'
  import DocumentListView from '../../components/common/DocumentListView.vue'
  import OptionModal from '../../components/quote/modals/send/option.vue'
  import SendModal from '../../components/quote/modals/send/send.vue'
  import CustomerModal from '../../components/quote/modals/customer/customer.vue'
  import DuplicateOptionModal from '../../components/quote/modals/duplicate/DuplicateOptionModal.vue'
  import Email from '../Email.vue'
  import { quoteDisplayTotal, quoteTotalsFromStored } from '../../helpers/quoteTotals'
  import { formatDocumentNumberShort } from '../../helpers/documentNumberDisplay'
  import { formatDisplayDate } from '../../helpers/displayDate'
  import { isQuoteStatusLocked, coerceQuoteStatusFields } from '../../helpers/quoteStatus'
  import { clone as deepClone } from '../../helpers/clone'

  export default {
    name: 'CustomerEdit',
    components: {
      QuotePreview,
      InvoicePreview,
      DocumentListView,
      OptionModal,
      DuplicateOptionModal,
      Email,
      'app-send-modal': SendModal,
      'app-customer': CustomerModal,
      'app-sub-navigation-bar': SubNavigationBar
    },
    data () {
      return {
        activeTab: 'details',
        submitted: false,
        postalSameAsPhysical: true,
        quoteListHeaders: ['Number', 'Customer', 'Issued', 'Expiry', 'Status', 'Amount'],
        invoiceListHeaders: ['Number', 'Customer', 'Issued', 'Due', 'Status', 'Amount'],
        contact: {
          id: null,
          company_id: null,
          company: '',
          first_name: '',
          last_name: '',
          email: '',
          phone: '',
          mobile: '',
          street_address: '',
          suburb: '',
          state: '',
          postcode: '',
          postal_street_address: '',
          postal_suburb: '',
          postal_state: '',
          postal_postcode: '',
          abn: '',
          acn: ''
        },
        quotes: [],
        quotesLoading: false,
        quotesPagination: null,
        invoices: [],
        invoicesLoading: false,
        invoicesPagination: null,
        previewQuote: null,
        previewQuoteItems: [],
        quotePreviewVisible: false,
        quoteOptionModalVisible: false,
        quoteSendModalVisible: false,
        duplicateChooserVisible: false,
        duplicateQuoteId: null,
        customerModalVisible: false,
        duplicating: false,
        previewInvoice: null,
        invoicePreviewVisible: false,
        invoiceOptionModalVisible: false,
        invoiceEmailVisible: false
      }
    },
    computed: {
      customerDisplayName () {
        return (this.contact && this.contact.company) ? this.contact.company : '-'
      },
      previewQuoteContact () {
        if (this.previewQuote && this.previewQuote.contact) return this.previewQuote.contact
        return this.contact || {}
      },
      quoteListRows () {
        const customerName = this.customerDisplayName
        return (this.quotes || []).map((q) => ({
          id: q.id,
          source: q,
          number: formatDocumentNumberShort(q.number) || '-',
          customerName,
          reference: q.reference,
          issuedDate: this.formatListDate(q.quote_date || q.date || q.created_at),
          secondaryDate: this.formatListDate(q.valid_until || q.due_date),
          status: this.quoteStatusKey(q.status),
          amountText: this.formatCurrency(quoteDisplayTotal(q))
        }))
      },
      invoiceListRows () {
        const customerName = this.customerDisplayName
        return (this.invoices || []).map((inv) => ({
          id: inv.id || inv.uuid,
          source: inv,
          number: formatDocumentNumberShort(inv.number) || '-',
          customerName,
          reference: inv.reference,
          issuedDate: this.formatListDate(inv.date || inv.invoice_date || inv.created_at),
          secondaryDate: this.formatListDate(inv.due_date || inv.invoice_due_date),
          status: this.invoiceStatusKey(inv.status),
          amountText: this.formatCurrency(quoteDisplayTotal(inv))
        }))
      }
    },
    watch: {
      activeTab (tab) {
        if (tab === 'quotes' && this.quotes.length === 0 && !this.quotesLoading) this.fetchQuotes(1)
        if (tab === 'invoices' && this.invoices.length === 0 && !this.invoicesLoading) this.fetchInvoices(1)
      }
    },
    methods: {
      formatListDate (val) {
        return formatDisplayDate(val, '-')
      },
      formatCurrency (num) {
        const n = Number(num)
        if (isNaN(n)) return '$0.00'
        return '$' + n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
      },
      quoteStatusKey (status) {
        const map = {
          1: 'draft',
          2: 'sent',
          3: 'accepted',
          4: 'accepted',
          5: 'invoiced',
          6: 'declined',
          draft: 'draft',
          sent: 'sent',
          accepted: 'accepted',
          declined: 'declined',
          rejected: 'declined',
          invoiced: 'invoiced'
        }
        if (status == null || status === '') return ''
        return map[status] || String(status).toLowerCase()
      },
      invoiceStatusKey (status) {
        return String(status || '').trim().toLowerCase()
      },
      onPreviewQuoteUpdate (quote) {
        if (!quote) return
        this.previewQuote = quote
        if (Array.isArray(quote.items)) this.previewQuoteItems = quote.items
      },
      onPreviewOptionChange (opt) {
        if (!opt || !this.previewQuote) return
        this.$set(this.previewQuote, 'active_option_id', opt.id)
        this.$set(this.previewQuote, 'active_option_index', opt.option_index)
        this.$set(this.previewQuote, 'last_active_option_id', opt.id)
        if (Array.isArray(opt.items)) {
          const items = deepClone(opt.items)
          this.previewQuote.items = items
          this.previewQuoteItems = items
        }
        if (opt.reference !== undefined) {
          this.$set(this.previewQuote, 'reference', opt.reference != null ? opt.reference : '')
        }
      },
      closeQuotePreview () {
        this.quotePreviewVisible = false
        if (this.activeTab === 'quotes') this.fetchQuotes((this.quotesPagination && this.quotesPagination.page) || 1)
      },
      sendQuoteButton () {
        this.savePreviewQuote(false, true)
      },
      sendQuoteBackButton () {
        this.quoteSendModalVisible = false
        this.quoteOptionModalVisible = false
        this.quotePreviewVisible = true
      },
      initDuplicate (quoteId) {
        this.duplicateQuoteId = quoteId
        this.duplicateChooserVisible = true
      },
      closeDuplicateChooser () {
        this.duplicateChooserVisible = false
      },
      onDuplicateVariation () {
        const quoteId = this.duplicateQuoteId || (this.previewQuote && this.previewQuote.id)
        if (quoteId == null || quoteId === '' || String(quoteId) === 'undefined') {
          this.errorNotification && this.errorNotification('Please save the quote before adding a variation')
          this.duplicateChooserVisible = false
          return
        }
        const sourceId = this.previewQuote && this.previewQuote.active_option_id
        this.duplicateChooserVisible = false
        axios.post(`/quote/${quoteId}/options`, { source_option_id: sourceId || null }).then((response) => {
          const options = response.data.options || []
          const newOpt = response.data.option
          this.$set(this.previewQuote, 'options', options)
          if (newOpt) {
            this.$set(this.previewQuote, 'active_option_id', newOpt.id)
            this.$set(this.previewQuote, 'active_option_index', newOpt.option_index)
            const full = options.find((o) => o.id === newOpt.id) || newOpt
            if (full && Array.isArray(full.items)) {
              this.previewQuote.items = full.items
              this.previewQuoteItems = full.items
            }
          }
          this.quotePreviewVisible = false
          this.$nextTick(() => {
            this.quotePreviewVisible = true
          })
        }).catch((err) => {
          const msg = err.response && err.response.data && err.response.data.error
            ? err.response.data.error
            : 'Failed to add variation'
          this.errorNotification && this.errorNotification(msg)
          this.quotePreviewVisible = true
        })
      },
      onDuplicateNewQuote () {
        this.duplicating = false
        this.duplicateChooserVisible = false
        this.quotePreviewVisible = false
        this.customerModalVisible = true
      },
      duplicateQuote (payload) {
        if (this.duplicating) return
        if (!payload || !payload.contact || !payload.contact.id || !this.duplicateQuoteId) {
          this.$nextTick(() => {
            if (this.$refs.duplicateCustomer && this.$refs.duplicateCustomer.resetSubmitting) {
              this.$refs.duplicateCustomer.resetSubmitting()
            }
          })
          return
        }
        this.duplicating = true
        this.customerModalVisible = false
        axios.post(`/quote/${this.duplicateQuoteId}/duplicate`, {
          contact_id: payload.contact.id,
          job_id: payload.service_m8_job
        }).then(response => {
          const id = (response.data && response.data.data && response.data.data.id)
            || (response.data && response.data.id)
          this.$router.push('/quote/' + id)
        }).catch(err => {
          this.duplicating = false
          this.customerModalVisible = true
          const msg = err.response && err.response.data && err.response.data.error
            ? err.response.data.error
            : 'Failed to duplicate quote'
          this.errorNotification && this.errorNotification(msg)
        })
      },
      savePreviewQuote (simple = true, silent = false, forcePersistLocked = false, optionsToSave = null) {
        return new Promise((resolve, reject) => {
          if (!this.previewQuote || !this.previewQuote.id) {
            return resolve({ quote: this.previewQuote, quote_items: this.previewQuoteItems || [] })
          }

          if (isQuoteStatusLocked(this.previewQuote) && !forcePersistLocked) {
            if (!simple && this.quotePreviewVisible) {
              this.quotePreviewVisible = false
              this.quoteOptionModalVisible = true
            }
            return resolve({ quote: this.previewQuote, quote_items: this.previewQuote.items || this.previewQuoteItems || [] })
          }

          if (this.previewQuote.due_date) {
            const dm = moment(String(this.previewQuote.due_date), ['YYYY-MM-DD', 'DD/MM/YYYY', 'DD/MM/YY'], true)
            this.previewQuote.valid_until = dm.isValid() ? dm.format('YYYY-MM-DD') : this.previewQuote.due_date
          }
          if (this.previewQuote.quote_date || this.previewQuote.date) {
            const raw = this.previewQuote.quote_date || this.previewQuote.date
            if (raw) {
              const m = moment(String(raw), ['YYYY-MM-DD', 'DD/MM/YYYY', 'DD/MM/YY'], true)
              if (m.isValid()) this.previewQuote.quote_date = m.format('YYYY-MM-DD')
            }
          }

          const opts = Array.isArray(this.previewQuote.options) ? this.previewQuote.options : []
          let optionIds = Array.isArray(optionsToSave) ? optionsToSave.map((id) => Number(id)).filter(Boolean) : null
          if (optionIds && optionIds.length > 1 && this.previewQuote.id) {
            const activeId = Number(this.previewQuote.active_option_id)
            const ordered = optionIds
              .map((id) => opts.find((o) => Number(o.id) === Number(id)))
              .filter(Boolean)
              .sort((a, b) => {
                if (Number(a.id) === activeId) return 1
                if (Number(b.id) === activeId) return -1
                return (a.option_index || 0) - (b.option_index || 0)
              })
            const runSequential = (index) => {
              const opt = ordered[index]
              const isLast = index === ordered.length - 1
              const payload = Object.assign({}, this.previewQuote, {
                items: opt.items || [],
                reference: opt.reference != null ? opt.reference : '',
                active_option_id: opt.id,
                active_option_index: opt.option_index,
                discount_mode: opt.discount_mode,
                discount_preset_id: opt.discount_preset_id,
                discount_title: opt.discount_title,
                discount_description: opt.discount_description,
                discount_type: opt.discount_type,
                discount_value: opt.discount_value,
                discount_amount_applied: opt.discount_amount_applied || 0,
                total: opt.total,
                total_amount: opt.total
              })
              coerceQuoteStatusFields(payload)
              const putSilent = !isLast || silent
              const putSimple = isLast ? simple : true
              return axios.put('/quote/' + this.previewQuote.id, payload).then((response) => {
                if (isLast) {
                  const respPayload = response.data.payload || {}
                  const saved = respPayload.quote || {}
                  if (saved.reference != null) this.previewQuote.reference = saved.reference
                  const savedOptions = respPayload.options || saved.options
                  if (Array.isArray(savedOptions) && savedOptions.length) {
                    this.$set(this.previewQuote, 'options', savedOptions)
                  }
                  this.previewQuote.items = (respPayload.quote_items && respPayload.quote_items.length)
                    ? respPayload.quote_items
                    : (this.previewQuoteItems || this.previewQuote.items || [])
                  this.previewQuoteItems = this.previewQuote.items
                  if (!putSimple && this.quotePreviewVisible) {
                    this.quotePreviewVisible = false
                    this.quoteOptionModalVisible = true
                  }
                  if (!putSilent) this.successNotification && this.successNotification()
                  return respPayload
                }
                return runSequential(index + 1)
              })
            }
            return runSequential(0).then(resolve).catch(reject).finally(() => {
              if (!silent) {
                this.quotePreviewVisible = false
                this.fetchQuotes((this.quotesPagination && this.quotesPagination.page) || 1)
              }
            })
          }

          const payload = Object.assign({}, this.previewQuote, {
            items: this.previewQuote.items || this.previewQuoteItems || [],
            active_option_id: this.previewQuote.active_option_id || null
          })
          coerceQuoteStatusFields(payload)
          axios.put('/quote/' + this.previewQuote.id, payload).then(response => {
            const respPayload = response.data.payload || {}
            const saved = respPayload.quote || {}
            if (saved.quote_date) this.previewQuote.quote_date = saved.quote_date
            if (saved.valid_until) {
              this.previewQuote.valid_until = saved.valid_until
              this.previewQuote.due_date = moment(saved.valid_until).format('YYYY-MM-DD')
            }
            if (saved.status) {
              this.previewQuote.status = saved.status
              const sm = { draft: 1, sent: 2, accepted: 3, rejected: 6, declined: 6, invoiced: 5 }
              this.previewQuote.status_id = sm[saved.status] || this.previewQuote.status_id
            }
            if (saved.reference != null) this.previewQuote.reference = saved.reference
            const savedOptions = respPayload.options || saved.options
            if (Array.isArray(savedOptions) && savedOptions.length) {
              this.$set(this.previewQuote, 'options', savedOptions)
            }
            if (saved.active_option_id) this.$set(this.previewQuote, 'active_option_id', saved.active_option_id)
            if (saved.active_option_index) this.$set(this.previewQuote, 'active_option_index', saved.active_option_index)

            if (!simple && this.quotePreviewVisible) {
              this.quotePreviewVisible = false
              this.quoteOptionModalVisible = true
            }

            this.previewQuote.items = (respPayload.quote_items && respPayload.quote_items.length)
              ? respPayload.quote_items
              : (this.previewQuoteItems || this.previewQuote.items || [])
            this.previewQuoteItems = this.previewQuote.items

            if (!silent) this.successNotification && this.successNotification()
            return resolve(respPayload)
          }).catch(reject).finally(() => {
            if (!silent) {
              this.quotePreviewVisible = false
              this.fetchQuotes((this.quotesPagination && this.quotesPagination.page) || 1)
            }
          })
        })
      },
      downloadQuotePdf () {
        if (!this.previewQuote || !this.previewQuote.id) return
        const isDraft = this.previewQuote.status_id === 1 || this.previewQuote.status_id === '1' || this.previewQuote.status === 'draft'
        if (isDraft) {
          this.previewQuote.status_id = 2
          this.previewQuote.status = 'sent'
        }
        this.quoteOptionModalVisible = false
        const optIdx = this.previewQuote.active_option_index || 1
        const fetchPdf = () => axios.get('/quote/' + this.previewQuote.id + '/pdf', { responseType: 'blob', params: { option: optIdx } })

        const pdfPromise = isQuoteStatusLocked(this.previewQuote)
          ? fetchPdf()
          : (() => {
            if (this.previewQuote.due_date) this.previewQuote.valid_until = this.previewQuote.due_date
            const payload = Object.assign({}, this.previewQuote)
            coerceQuoteStatusFields(payload)
            return axios.put('/quote/' + this.previewQuote.id, payload).then(() => fetchPdf())
          })()

        pdfPromise.then(response => {
          const file = new Blob([response.data], { type: 'application/pdf' })
          const fileURL = URL.createObjectURL(file)
          const a = document.createElement('a')
          document.body.appendChild(a)
          a.style = 'display: none'
          a.href = fileURL
          a.download = `${this.previewQuote.number}-${optIdx}.pdf`
          a.click()
          window.URL.revokeObjectURL(fileURL)
          this.swal && this.swal({ type: 'success', title: 'Quote downloaded' })
        }).catch(err => {
          this.errorNotification(err.response && err.response.data && err.response.data.error ? err.response.data.error : (err.message || 'Download failed'))
        }).finally(() => {
          this.fetchQuotes((this.quotesPagination && this.quotesPagination.page) || 1)
        })
      },
      viewQuote (id) {
        if (id == null || id === 'new' || String(id).trim() === '') return
        axios.get('/quote/' + id).then(res => {
          const data = res.data
          const quote = data.quote || data
          const statusIdMap = { draft: 1, sent: 2, accepted: 3, invoiced: 5, declined: 6, rejected: 6 }
          const normalizedStatusId = (() => {
            if (quote.status_id != null && quote.status_id !== '') return Number(quote.status_id)
            const s = quote.status != null ? String(quote.status).toLowerCase() : ''
            return statusIdMap[s] || 1
          })()
          const storedTotals = quoteTotalsFromStored(quote)
          const contactPayload = quote.contact || this.contact || {}
          this.previewQuote = Object.assign({}, quote, {
            status_id: normalizedStatusId,
            sub_total: storedTotals.sub_total,
            gst_total: storedTotals.gst_total,
            total: storedTotals.total,
            discount_override: (data.quote || data).discount_amount_applied || (data.quote || data).discount || 0,
            discount_mode: (data.quote || data).discount_mode || null,
            discount_preset_id: (data.quote || data).discount_preset_id || null,
            discount_title: (data.quote || data).discount_title || null,
            discount_description: (data.quote || data).discount_description || null,
            discount_type: (data.quote || data).discount_type || null,
            discount_value: (data.quote || data).discount_value || null,
            discount_amount_applied: (data.quote || data).discount_amount_applied || 0,
            contact: contactPayload
          })
          coerceQuoteStatusFields(this.previewQuote)
          this.previewQuoteItems = Array.isArray(quote.items) ? quote.items : []
          this.quotePreviewVisible = true
        }).catch(() => {})
      },
      viewInvoice (inv) {
        if (!inv || !inv.uuid) return
        axios.get(`/invoice/${inv.uuid}/items`).then(response => {
          const payload = response.data.data || {}
          const dateOnly = (v) => (v && String(v).slice) ? String(v).slice(0, 10) : ''
          const listContact = (inv && inv.contact) ? inv.contact : {}
          const payloadContact = (payload.contact && typeof payload.contact === 'object') ? payload.contact : {}
          const contact = Object.assign({}, this.contact || {}, listContact, payloadContact, {
            email: payloadContact.email || listContact.email || (this.contact && this.contact.email) || payload.contact_email || ''
          })
          const contactName = [contact.first_name, contact.last_name].filter(Boolean).join(' ').trim()
            || contact.company
            || contact.name
            || '—'
          this.previewInvoice = Object.assign({}, inv, {
            items: payload.items || [],
            terms: payload.terms,
            total: payload.total,
            sub_total: payload.sub_total,
            gst_total: payload.gst_total,
            discount: payload.discount,
            discount_mode: payload.discount_mode,
            discount_title: payload.discount_title,
            discount_description: payload.discount_description,
            discount_type: payload.discount_type,
            discount_value: payload.discount_value,
            discount_amount_applied: payload.discount_amount_applied,
            company_id: payload.company_id,
            invoice_signed_by_name: payload.invoice_signed_by_name,
            invoice_signature_data: payload.invoice_signature_data,
            invoice_signed_at: payload.invoice_signed_at,
            date: dateOnly(inv.created_at || inv.date || payload.date),
            due_date: inv.due_date ? dateOnly(inv.due_date) : dateOnly(payload.due_date),
            contact: Object.assign({}, contact, { name: contactName }),
            contact_email: contact.email || ''
          })
          this.invoicePreviewVisible = true
        }).catch(() => {})
      },
      closeInvoicePreview (event) {
        this.invoicePreviewVisible = false
        if (event && event.refresh && this.activeTab === 'invoices') {
          this.fetchInvoices((this.invoicesPagination && this.invoicesPagination.page) || 1)
        } else if (this.activeTab === 'invoices') {
          this.fetchInvoices((this.invoicesPagination && this.invoicesPagination.page) || 1)
        }
      },
      openInvoiceSendOption (item) {
        if (item) this.previewInvoice = item
        this.invoicePreviewVisible = false
        this.invoiceOptionModalVisible = true
      },
      openInvoiceEmail () {
        this.invoiceOptionModalVisible = false
        this.invoiceEmailVisible = true
      },
      closeInvoiceEmail (event) {
        if (event && event.refresh) {
          this.fetchInvoices((this.invoicesPagination && this.invoicesPagination.page) || 1)
        }
        this.invoiceEmailVisible = false
      },
      onInvoiceEmailSent () {
        this.fetchInvoices((this.invoicesPagination && this.invoicesPagination.page) || 1)
        this.closeInvoiceEmail()
      },
      downloadInvoicePdf () {
        this.invoiceOptionModalVisible = false
        const item = this.previewInvoice
        if (!item || !item.uuid) return
        const status = String(item.status || '').toLowerCase()
        const beforePdf = status === 'draft'
          ? axios.patch(`/invoice/${item.uuid}`, { status: 'sent' }).catch(() => {})
          : Promise.resolve()
        beforePdf.then(() => {
          return axios.get(`/invoice/${item.uuid}/pdf`, { responseType: 'blob' })
        }).then((response) => {
          const file = new Blob([response.data], { type: 'application/pdf' })
          const url = URL.createObjectURL(file)
          const a = document.createElement('a')
          document.body.appendChild(a)
          a.style = 'display: none'
          a.href = url
          a.download = `${item.number || 'invoice'}.pdf`
          a.click()
          URL.revokeObjectURL(url)
          document.body.removeChild(a)
          this.fetchInvoices((this.invoicesPagination && this.invoicesPagination.page) || 1)
        }).catch(() => {})
      },
      fetchContact () {
        const id = this.$route.params.id
        if (!id) return
        axios.get('/api/contact/' + id)
          .then(res => {
            const c = res.data.contact || res.data
            this.contact = Object.assign({}, this.contact, c, {
              postal_street_address: c.postal_street_address != null ? c.postal_street_address : '',
              postal_suburb: c.postal_suburb != null ? c.postal_suburb : '',
              postal_state: c.postal_state != null ? c.postal_state : '',
              postal_postcode: c.postal_postcode != null ? c.postal_postcode : ''
            })
            this.postalSameAsPhysical = !(c.postal_street_address || c.postal_suburb || c.postal_state || c.postal_postcode)
          })
          .catch(err => {
            if (err.response && err.response.status === 404) this.$router.push('/contact')
            else this.errorNotification(err.response && err.response.data && err.response.data.error ? err.response.data.error : 'Failed to load customer')
          })
      },
      fetchQuotes (page = 1) {
        this.quotesLoading = true
        axios.get('/api/contact/' + this.$route.params.id + '/quotes', { params: { page } })
          .then(res => {
            this.quotes = res.data.quotes || []
            this.quotesPagination = res.data.pagination || null
          })
          .catch(() => { this.quotes = []; this.quotesPagination = null })
          .finally(() => { this.quotesLoading = false })
      },
      fetchInvoices (page = 1) {
        this.invoicesLoading = true
        axios.get('/api/contact/' + this.$route.params.id + '/invoices', { params: { page } })
          .then(res => {
            this.invoices = res.data.invoices || []
            this.invoicesPagination = res.data.pagination || null
          })
          .catch(() => { this.invoices = []; this.invoicesPagination = null })
          .finally(() => { this.invoicesLoading = false })
      },
      save () {
        this.submitted = true
        if (!this.contact.company || !this.contact.company.trim()) {
          this.errorNotification('Customer name is required')
          return
        }
        if (!this.contact.email || !this.contact.email.trim()) {
          this.errorNotification('Email address is required')
          return
        }
        const payload = Object.assign({}, this.contact)
        if (this.postalSameAsPhysical) {
          payload.postal_street_address = ''
          payload.postal_suburb = ''
          payload.postal_state = ''
          payload.postal_postcode = ''
        }
        axios.put('/api/contact/' + this.contact.id, payload)
          .then(() => {
            this.successNotification('Customer updated successfully')
            this.$router.push('/contact')
          })
          .catch(err => {
            const msg = err.response && err.response.data && err.response.data.error ? err.response.data.error : (err.message || 'Failed to update customer')
            this.errorNotification(msg)
          })
      },
      deleteCustomer () {
        const name = this.contact.company || (this.contact.first_name + ' ' + this.contact.last_name) || 'this customer'
        swal({
          title: 'Delete Confirmation',
          html: `This will delete <strong>${name}</strong> and all associated details. This action cannot be undone.`,
          type: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#d33',
          confirmButtonText: 'Yes, delete!',
          cancelButtonText: 'Cancel'
        }).then((result) => {
          if (result.value) {
            axios.delete('/api/contact/' + this.contact.id)
              .then(() => {
                this.successNotification('Customer deleted')
                this.$router.push('/contact')
              })
              .catch(err => {
                this.errorNotification(err.response && err.response.data && err.response.data.error ? err.response.data.error : 'Failed to delete')
              })
          }
        })
      }
    },
    created () {
      this.fetchContact()
    },
    mounted () {
      this.$nextTick(() => { $(this.$el).find('.ui.checkbox').checkbox() })
    }
  }
</script>

<style scoped>
.page-with-subnav__content.main-container {
  margin-top: 16px;
}
.customer-form-grid {
  max-width: 680px;
  margin-left: 0;
  margin-right: 0;
}
.customer-form-grid--documents {
  max-width: none;
}
.customer-documents-tab {
  padding-left: 0 !important;
  padding-right: 0 !important;
}
</style>
